import { getExternalRequestEnv } from 'constants/externalRequest';

import type { AssetType } from 'types/project';
import type { Building } from 'types/configurations';
import type { IUser } from 'types/auth';
import type { Quote } from 'types/quote';
import type { EnvironmentState } from 'store/slices/environment/slice';
import type { EnrichedExtraSpace } from 'store/sagas/configurations/configurations.api';

export type ExternalRequestSpace = {
  code?: string;
  name: string;
  building: Building;
};

export type ExternalRequestBody = {
  env: string;
  request_id: string;
  project_id: string;
  client: {
    email: string;
    firstName: string;
    lastName: string;
    tel: string;
  };
  space: {
    apartment: {
      code: string;
      name: string;
      spaceType: AssetType;
    };
    building: {
      code: string;
      name: string;
      complex: string;
    };
    extras: Array<{
      code: string;
      name: string;
      spaceType: string;
    }>;
  };
  quote: {
    name: string;
    price: string;
    checkIn: string;
    checkOut: string;
  };
  contract_url: string;
  declined_url: string;
  language: string;
};

type BuildExternalRequestBodyParams = {
  quote: Quote;
  projectId: string;
  user: IUser;
  space: ExternalRequestSpace;
  assetTypology: AssetType;
  clientApiBaseUrl: string;
  namespace: EnvironmentState['REACT_APP_NAMESPACE'] | undefined;
  extras?: EnrichedExtraSpace[];
};

export const buildExternalRequestBody = ({
  quote,
  projectId,
  user,
  space,
  assetTypology,
  clientApiBaseUrl,
  namespace,
  extras = [],
}: BuildExternalRequestBodyParams): ExternalRequestBody => {
  // Same amount shown in CardQuote: price || rentPayments.total
  const price = String(quote.price || (quote.rentPayments?.total ?? 0));
  const baseUrl = clientApiBaseUrl.replace(/\/$/, '');

  return {
    env: getExternalRequestEnv(namespace),
    request_id: quote.id,
    project_id: projectId,
    client: {
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      tel: user.tel,
    },
    space: {
      apartment: {
        code: space.code ?? '',
        name: space.name,
        spaceType: assetTypology,
      },
      building: {
        code: space.building.code,
        name: space.building.name,
        complex: space.building.complex,
      },
      extras: extras.map((extra) => ({
        code: extra.code,
        name: extra.name,
        spaceType: extra.type,
      })),
    },
    quote: {
      name: quote.name,
      price,
      checkIn: quote.dateRange?.checkInDate ?? '',
      checkOut: quote.dateRange?.checkOutDate ?? '',
    },
    contract_url: `${baseUrl}/v1/request/rent/accept-application/${quote.id}`,
    declined_url: `${baseUrl}/v1/request/rent/decline-application/${quote.id}`,
    language: user.notificationLanguage ?? 'it',
  };
};
